/**
 * Helpers for pushing server-returned field errors onto a TanStack Form so they render inline.
 *
 * `form.setErrorMap` is keyed by validation CAUSE (`onChange` / `onSubmit` / `onServer` / …), and
 * each value must be a `GlobalFormValidationError` (`{ form?, fields }`) for the error to be
 * distributed to individual fields. Passing a bare field map like `{ name: 'msg' }` silently lands
 * `name` as a bogus cause key and never reaches the field — and `setErrorMap({})` is a no-op
 * because it iterates zero entries. These helpers encapsulate the correct shape in one place.
 *
 * We use the `onServer` cause: it's the idiomatic bucket for server-returned errors and won't be
 * clobbered by `onChange` / `onSubmit` validators that recompute their own buckets.
 */

/**
 * Minimal structural type for the bit of the form API we use. `setErrorMap`'s real signature is a
 * deep generic that differs per form, so we accept any callable and re-narrow it inside (the
 * runtime shape is locked by form-errors.test.ts).
 */
type FormErrorMapSetter = { setErrorMap: (...args: never[]) => void };

type ServerErrorMap = { onServer: { fields: Record<string, string> } };

function setServerErrorMap(form: FormErrorMapSetter, fields: Record<string, string>): void {
  (form.setErrorMap as unknown as (errorMap: ServerErrorMap) => void)({ onServer: { fields } });
}

/** Surface server-returned field errors inline (keyed by field name). */
export function applyServerFieldErrors(form: FormErrorMapSetter, fields: Record<string, string>): void {
  setServerErrorMap(form, fields);
}

/** Clear any server-returned field errors previously set by {@link applyServerFieldErrors}. */
export function clearServerFieldErrors(form: FormErrorMapSetter): void {
  setServerErrorMap(form, {});
}
